import {
  AppWindow,
  Bot,
  SquareLibrary,
  BugIcon,
  Route,
  ChevronRight,
  HardDrive,
  ListTree,
  KeySquare,
  Mail,
  Aperture,
  Settings,
  Boxes,
  UsersRound,
  BookUser,
  EthernetPort,
} from "lucide-react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SidebarNavUser } from "./user-nav";
import { TeamSwitcher } from "./team-switcher";
import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useSidebar } from "@/components/ui/sidebar";
import { motion, AnimatePresence } from "framer-motion";
import logoCropped from "@/assets/logo_cropped.png";

export const groups = [
  {
    title: "Overview",
    items: [
      {
        title: "Gateways",
        url: "/gateways",
        icon: HardDrive,
        isActive: false,
        shortcut: "G",
      },
      {
        title: "Divergence Reports",
        url: "/divergences",
        icon: BugIcon,
        isActive: false,
        shortcut: "D",
      },
      {
        title: "Licenses Management",
        // items: [
        //   {
        //     title: "Licenses",
        //     url: "/licenses-management/licenses",
        //     isActive: false,
        //   },
        //   {
        //     title: "Expiration warning",
        //     url: "/licenses-management/expiration-warning",
        //     isActive: false,
        //   },
        // ],
        url: "/licenses-management",
        icon: KeySquare,
        isActive: false,
      },
    ],
  },
  {
    title: "User Management",
    items: [
      {
        title: "Users",
        url: "/users",
        icon: UsersRound,
        isActive: true,
        shortcut: "U",
      },
      {
        title: "Organisations",
        url: "/organisations",
        icon: Boxes,
        isActive: false,
        shortcut: "O",
      },
      {
        title: "Business Directory",
        url: "/business-directory",
        icon: SquareLibrary,
        isActive: false,
        shortcut: "B",
      },
      {
        title: "User Labels",
        url: "/user-labels",
        icon: BookUser,
        isActive: false,
        shortcut: "P",
      },
    ],
  },
  {
    title: "Call Management",
    items: [
      {
        title: "Queue Management",
        icon: Route,
        isActive: false,
        shortcut: "Q",
        items: [
          {
            title: "Queues",
            url: "/queues",
            isActive: false,
          },
          {
            title: "Queue Groups",
            url: "/queue-groups",
            isActive: false,
          },
        ],
      },
      {
        title: "Callscreening",
        url: "/callscreening",
        icon: Aperture,
        isActive: false,
        shortcut: "C",
      },
      {
        title: "IVR",
        url: "/ivr",
        icon: ListTree,
        isActive: false,
        shortcut: "I",
      },
    ],
  },
  {
    title: "System",
    items: [
      {
        title: "Settings",
        url: "/settings",
        icon: Settings,
        isActive: false,
      },
      {
        title: "Administrators",
        url: "/administrators",
        icon: Bot,
        isActive: false,
        items: [
          {
            title: "Accounts",
            url: "/administrators/accounts",
            isActive: false,
          },
          {
            title: "Profiles",
            url: "/administrators/profiles",
            isActive: false,
          },
        ],
      },
      //TODO: Readd this when we have the emails pages
      // {
      //   title: "E-mails",
      //   items: [
      //     {
      //       title: "System",
      //       url: "/emails/system",
      //       isActive: false,
      //     },
      //     {
      //       title: "Organisations",
      //       url: "/emails/organisations",
      //       isActive: false,
      //     },
      //   ],
      //   url: "",
      //   icon: Mail,
      //   isActive: false,
      // },
      {
        title: "SBC Management",
        url: "/sbc-management",
        icon: EthernetPort,
        isActive: false,
        // items: [
        //   {
        //     title: "SBCs",
        //     url: "/sbc-management/SBCs",
        //     isActive: false,
        //   },
        //   {
        //     title: "Numbers",
        //     url: "/sbc-management/numbers",
        //     isActive: false,
        //   },
        // ],
      },
      {
        title: "Client Management",
        url: "/client-management",
        icon: AppWindow,
        isActive: false,
      },
    ],
  },
];

export function AppSidebar({ user }: any) {
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const sidebarContentRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLAnchorElement>(null);
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  const { isMobile, state, toggleSidebar } = useSidebar();

  const isPathActive = (url: string) => url && currentPath.startsWith(url);
  const isSubmenuActive = (items: any[]) => items.some((sub) => isPathActive(sub.url));

  useEffect(() => {
    const open = groups.flatMap((g: any) => g.items).find((i: any) => i.items && isSubmenuActive(i.items));
    setOpenItem(open ? open.title : null);
  }, [currentPath]);

  const scrollToActiveItem = () => {
    const el = activeItemRef.current;
    const container = sidebarContentRef.current;
    if (!el || !container) return;
    const rect = el.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    if (rect.top < cRect.top) container.scrollTop -= cRect.top - rect.top + 16;
    if (rect.bottom > cRect.bottom) container.scrollTop += rect.bottom - cRect.bottom + 16;
  };

  useEffect(scrollToActiveItem, [currentPath]);

  const handleLinkClick = (event?: React.MouseEvent<HTMLAnchorElement>, url?: string) => {
    if (isMobile) toggleSidebar();

    // Handle submenu toggle for routes that have submenus when the same route is clicked
    if (url && isPathActive(url)) {
      // Check if this is a route that should have a submenu (like client-management)
      if (url === "/client-management") {
        // We need to access the dashboard context to toggle the submenu
        // Since we don't have direct access here, we'll trigger a custom event
        window.dispatchEvent(new CustomEvent("toggleSubmenu", { detail: { route: url } }));
      }
      // If already on settings, force a reset in the settings route
      if (url === "/settings") {
        window.dispatchEvent(new CustomEvent("forceSettingsReset"));
      }
    }
  };

  return (
    <>
      {/* Backdrop for mobile */}
      <AnimatePresence>
        {isMobile && state !== "expanded" && (
          <motion.div
            className="fixed inset-0 bg-black/40 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => toggleSidebar()}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        className={`fixed md:relative z-50 h-full bg-sidebar w-64 md:w-auto `}
        initial={{ x: isMobile ? "-100%" : 0, opacity: 1 }}
        animate={
          isMobile ? (state !== "expanded" ? { x: 0, opacity: 1 } : { x: "-100%", opacity: 1 }) : { x: 0, opacity: 1 }
        }
        transition={{ type: "tween", duration: 0.25 }}
      >
        <Sidebar collapsible="icon">
          <SidebarHeader>
            {/* Keep this until we have a way to change server? */}
            {/* <TeamSwitcher teams={environmentData.teams} /> */}
            <div className="flex justify-center py-4">
              <img
                src={logoCropped}
                alt="Logo"
                className="w-40 mix-blend-multiply dark:mix-blend-normal dark:invert classic:mix-blend-normal classic:invert"
              />
            </div>
          </SidebarHeader>

          <SidebarContent ref={sidebarContentRef} className="overflow-y-auto">
            <div className="space-y-1">
              {groups.map((group) => (
                <SidebarGroup key={group.title}>
                  {group.title && <SidebarGroupLabel>{group.title}</SidebarGroupLabel>}
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {group.items.map((item) =>
                        item.items ? (
                          isMobile || state !== "collapsed" ? (
                            <Collapsible
                              key={item.title}
                              open={openItem === item.title}
                              onOpenChange={(o) => setOpenItem(o ? item.title : null)}
                            >
                              <SidebarMenuItem>
                                <CollapsibleTrigger asChild>
                                  <SidebarMenuButton
                                    tooltip={item.title}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setOpenItem(openItem === item.title ? null : item.title);
                                    }}
                                    className={
                                      openItem === item.title || isSubmenuActive(item.items)
                                        ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                        : ""
                                    }
                                  >
                                    {item.icon && <item.icon />}
                                    <span>{item.title}</span>
                                    <ChevronRight
                                      className={`ml-auto transition-transform ${openItem === item.title ? "rotate-90" : ""
                                        }`}
                                    />
                                  </SidebarMenuButton>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <SidebarMenuSub>
                                    {item.items.map((subItem) => (
                                      <SidebarMenuSubItem key={subItem.title}>
                                        <SidebarMenuSubButton asChild>
                                          <Link
                                            to={subItem.url}
                                            onClick={handleLinkClick}
                                            className={
                                              isPathActive(subItem.url)
                                                ? "bg-sidebar-primary text-sidebar-primary-foreground"
                                                : "hover:bg-sidebar-accent hover:text-sidebar-foreground"
                                            }
                                          >
                                            {subItem.title}
                                          </Link>
                                        </SidebarMenuSubButton>
                                      </SidebarMenuSubItem>
                                    ))}
                                  </SidebarMenuSub>
                                </CollapsibleContent>
                              </SidebarMenuItem>
                            </Collapsible>
                          ) : (
                            <SidebarMenuItem key={item.title}>
                              <Popover
                                open={openPopover === item.title}
                                onOpenChange={(open) => setOpenPopover(open ? item.title : null)}
                              >
                                <PopoverTrigger asChild>
                                  <SidebarMenuButton
                                    tooltip={item.title}
                                    className={isSubmenuActive(item.items) ? "bg-sidebar-accent" : ""}
                                  >
                                    {item.icon && <item.icon />}
                                    <span>{item.title}</span>
                                  </SidebarMenuButton>
                                </PopoverTrigger>
                                <PopoverContent side="right" align="start" className="w-48 p-1">
                                  <div className="space-y-0.5">
                                    {item.items.map((subItem) => (
                                      <Link
                                        key={subItem.title}
                                        to={subItem.url}
                                        onClick={() => setOpenPopover(null)}
                                        className={`flex items-center px-3 py-2 text-sm text-color-red rounded-sm transition-colors ${isPathActive(subItem.url)
                                          ? "bg-primary text-primary-foreground"
                                          : "hover:bg-accent hover:text-accent-foreground"
                                          }`}
                                      >
                                        {subItem.title}
                                      </Link>
                                    ))}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </SidebarMenuItem>
                          )
                        ) : (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton asChild tooltip={item.title}>
                              <Link
                                to={item.url}
                                onClick={(e) => handleLinkClick(e, item.url)}
                                activeProps={{
                                  className:
                                    "bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 hover:text-sidebar-primary-foreground",
                                }}
                                inactiveProps={{
                                  className: "hover:bg-sidebar-accent hover:text-sidebar-foreground",
                                }}
                                ref={isPathActive(item.url) ? activeItemRef : undefined}
                              >
                                <item.icon />
                                <span>{item.title}</span>
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        )
                      )}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              ))}
            </div>
          </SidebarContent>

          <SidebarFooter>
            <SidebarNavUser user={user} />
          </SidebarFooter>
        </Sidebar>
      </motion.div>
    </>
  );
}
